<script lang="ts" module>
  type Sticker = {
    image: {
      src: string;
      alt: string;
    };
    name: string;
    type?: string;
    formattedSize: string;
  };

  type CustomSticker = {
    svg: {
      parts: Array<Part>;
      productItem: ProductItem;
    };
    name: string;
    type?: string;
    formattedSize: string;
  };

  export type AddedItem = Sticker | CustomSticker;

  export const getAddedCustomSticker = (
    customSticker: CustomStickerWithId,
    productNameTranslator: (key: string) => string,
    customStickerTranslation: string,
    locale: LocalizationContext,
  ): CustomSticker => ({
    svg: {
      parts: customSticker.parts as Array<Part>,
      productItem: customSticker.productItem as ProductItem,
    },
    name: productNameTranslator(customSticker.productItem.productName),
    type: customStickerTranslation,
    formattedSize: formatSize(
      customSticker.attributes.width as number,
      customSticker.attributes.height as number,
      locale,
      true,
    ),
  });
</script>

<script lang="ts">
  import { Button } from '@athena-lib/Button';

  import type { BuyStickerCardProps } from '@athena-lib/BuyStickerCard';
  import BuyStickerCard from '@athena-lib/BuyStickerCard/BuyStickerCard.svelte';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import { Grid, GridItem } from '@athena-lib/Grid';
  import type { LocalizationContext } from '@athena-lib/i18n';
  import Image from '@athena-lib/Image/Image.svelte';
  import { LiveLineItemStickerPreview } from '@athena-lib/LivePreview';
  import { formatSize } from '@athena-lib/ProductWizard';
  import type { ProductItem } from '@athena-lib/ProductWizard/product/model/ProductItem';
  import type { Part } from '@athena-lib/SvgPreview/types';
  import { Text } from '@athena-lib/Text';
  import HeadingMedium from '@athena-lib/Text/HeadingMedium/HeadingMedium.svelte';
  import HeadingSmall from '@athena-lib/Text/HeadingSmall/HeadingSmall.svelte';
  import type { CustomStickerWithId } from '@stickerapp-org/checkout-sdk';

  const {
    addedItem,
    upsellItems,
    onClickCartLink,
  }: {
    addedItem: AddedItem;
    upsellItems: Array<BuyStickerCardProps>;
    onClickCartLink?: () => void;
  } = $props();

  const commonTranslations = getCommonTranslations();
</script>

<div class="flex h-full flex-col px-5 pb-5 pt-14 text-center">
  <HeadingMedium size="small" class="text-center">{commonTranslations.youHaveAdded}</HeadingMedium>
  <div class="mt-4 flex flex-col items-center gap-1 text-center">
    {#if 'svg' in addedItem}
      <LiveLineItemStickerPreview
        productItem={addedItem.svg.productItem as ProductItem}
        parts={addedItem.svg.parts as Array<Part>}
      />
    {:else}
      <Image src={addedItem.image.src} alt={addedItem.image.alt} />
    {/if}

    <Text bold>{addedItem.name}</Text>
    {#if addedItem.type}
      <Text size="small">{addedItem.type}</Text>
    {/if}
    <Text size="small">{addedItem.formattedSize}</Text>
  </div>
  {#if upsellItems.length > 0}
    <hr />
    <HeadingSmall size="xxsmall">{commonTranslations.weAlsoRecommend}</HeadingSmall>
    <Grid space="gutter" columns={2}>
      {#each upsellItems as upsellItem}
        <GridItem>
          <BuyStickerCard {...upsellItem} small />
        </GridItem>
      {/each}
    </Grid>
  {/if}
  {#if onClickCartLink}
    <div class="mt-auto flex justify-center">
      <Button onClick={onClickCartLink}>
        {commonTranslations.viewCart}
      </Button>
    </div>
  {/if}
</div>
